import React, { useCallback, useMemo, useState } from "react";
import {
    Container,
} from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { getLocalStorageItem,setLocalStorageItem} from "../../_helpers";
import { apps, LOCAL_STORAGE_GRID_KEYS, userAccountRoleConstants} from "../../_constants";
import DataGridV2 from "../../components/DataGridV2";
import { INITIAL_ROW_SELECTION_MODEL } from "../../components/DataGridV2/constants";
import { useGridApiRef } from "@mui/x-data-grid-pro";
import { getObjectGridColumns } from "./objectGridUtils";
import { Add } from "@mui/icons-material";
import { getIdsForGridSelectedRows } from "../../_helpers/getIdsForGridSelectedRows";
import QuestionnaireCreationModal from "../Questionnaire/QuestionnaireCreation/QuestionnaireCreationModal";

const ObjectGrid = (props) => {
    const apiRef = useGridApiRef();
    const app = useSelector((state) => state.app);
    const { t, i18n, } = useTranslation();
    const classes = useStyles();
    const { all, loading: buildingLoading } = useSelector(
        (state) => state.buildings
    );
    const keyPrefix = app === apps.constructionQuality ? "CONSTRUCTION_QUALITY" : "GENERAL";
    const canCreateQuestionnaire = all.some(a => a.roles?.includes(userAccountRoleConstants.siteManager)) && app === apps.constructionQuality;  
    const [rowSelectionModel, setRowSelectionModel] = useState(INITIAL_ROW_SELECTION_MODEL);
    const [isSurveyCreationModalOpen, setIsSurveyCreationModalOpen] = useState(false)
    const [paginationModel, setPaginationModel] = useState(() => getLocalStorageItem(
        LOCAL_STORAGE_GRID_KEYS.OBJECTS_PAGINATION[keyPrefix], undefined
    ))

    const onPaginationModelChange = useCallback((newPaginationModel) => {
        setPaginationModel(newPaginationModel)
        setLocalStorageItem(
            LOCAL_STORAGE_GRID_KEYS.OBJECTS_PAGINATION[keyPrefix],
            newPaginationModel
        );
    }, [keyPrefix])

    const getObjectsForPrintAndDownload = ({ isAllSelected = true, idsToExport = [] }) => {
        const userSelectedRows = isAllSelected ? all : idsToExport.map(id => all?.find(item => item?.buildingId === id));
        return userSelectedRows;
    };
    const statusOptions = [
        ...new Set(all.map((item) => item.status))
    ];
    const columns = getObjectGridColumns({ t, keyPrefix, i18n, apiRef, statusOptions })
    const rows = useMemo(
        () =>
            all?.map((item) => ({
                ...item,
                id: item.buildingId,
            })) ?? [],
        [all]
    );
    const handleSurveyCreationModalClose = () => {
        setIsSurveyCreationModalOpen(false);
    };

    const buildingIds =  useMemo(() => {
      return getIdsForGridSelectedRows(rowSelectionModel,all)
     },[rowSelectionModel, all]);
       
    const getToolbarProps = useMemo(() => {
        const hasSelectedRows = buildingIds.length > 0
        const selectedBuildings = all.filter(b => buildingIds.includes(b.buildingId));
        const isCreateQuestionnaireAllowed = selectedBuildings.every( b => b.roles?.includes(userAccountRoleConstants.siteManager));
      
        const toolbarProps = {
            title: t("general.objects"),
            getData: getObjectsForPrintAndDownload,
            taskMenuListItems: hasSelectedRows && canCreateQuestionnaire && isCreateQuestionnaireAllowed
                ? [
                    {
                        title: t("questionnaire.addQuestionnaire"),
                        icon: (props) => <Add {...props} />,
                        onClick: () => setIsSurveyCreationModalOpen(true),
                        closeTaskMenu: true,
                    },
                ]
                : [],
        };
        return toolbarProps;
    }, [rowSelectionModel,canCreateQuestionnaire, t, all,buildingIds]);
   

    return (
        <Container maxWidth={false} className={classes.mainContainer}>
            <DataGridV2
                localSearchIdentifier={LOCAL_STORAGE_GRID_KEYS.OBJECTS_SEARCH_TEXT[keyPrefix]}
                localColumnFilterIdentifier={LOCAL_STORAGE_GRID_KEYS.OBJECTS_COLUMN_FILTER[keyPrefix]}
                localColumnSortIdentifier={LOCAL_STORAGE_GRID_KEYS.OBJECTS_COLUMN_SORT[keyPrefix]}
                localColumnOrderIdentifier={LOCAL_STORAGE_GRID_KEYS.OBJECTS_COLUMN_ORDER[keyPrefix]}
                localColumnWidthIdentifier={LOCAL_STORAGE_GRID_KEYS.OBJECTS_COLUMN_WIDTH[keyPrefix]}
                localColumnVisibilityIdentifier={LOCAL_STORAGE_GRID_KEYS.OBJECTS_COLUMN_VISIBILITY[keyPrefix]}
                apiRef={apiRef}
                loading={buildingLoading}
                checkboxSelection
                rows={rows}
                columns={columns}
                pagination
                filterMode="client"
                {...(!!paginationModel && { paginationModel })}
                onPaginationModelChange={onPaginationModelChange}
                rowSelectionModel={rowSelectionModel}
                onRowSelectionModelChange={(newRowSelectionModel) => {
                    setRowSelectionModel(newRowSelectionModel);
                }}
                toolbarProps={{
                    ...getToolbarProps,
                    selectedRows: rowSelectionModel,
                    getData: getObjectsForPrintAndDownload
                }}
            />

            {isSurveyCreationModalOpen && (
                <QuestionnaireCreationModal
                    handleClose={() => handleSurveyCreationModalClose()}    
                    buildingId={buildingIds}
                />
            )}
          </Container>
    );
};

const useStyles = makeStyles((theme) => ({
    mainContainer: {
        height: "100%",
        width: "100%",
        overflow: "auto",
        padding: 0,
    },
}));

export default ObjectGrid;
